import type { SidebarCategory, SidebarPage } from '@readme/backend/iso';
import type { ReactNode, ReactElement } from 'react';

import { memo, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProjectContext } from '@core/context'; // eslint-disable-line no-restricted-imports
import useClassy from '@core/hooks/useClassy';
import useLocalStorage from '@core/hooks/useLocalStorage';
import useMediaQuery from '@core/hooks/useMediaQuery';
import usePlanPermissions from '@core/hooks/usePlanPermissions';
import { useProjectStore, useSuperHubStore } from '@core/store';
import { isApiConfigCategory } from '@core/store/SuperHub/Sidebar/util';
import classy from '@core/utils/classy';
import Button from '@ui/Button';
import Icon from '@ui/Icon';

import Group from './Group';
import SidebarList from './List';
import classes from './style.module.scss';
import './style.scss';
import useScrollToActiveSidebarItem from './useScrollToActiveSidebarItem';

export interface SidebarProps {
  /**
   * Slug for the current page.
   */
  activeDoc?: string;

  /**
   * Collection of ReadMe docs or endpoint reference pages.
   */
  categories: SidebarCategory[];

  className?: string;

  /**
   * Optional custom icon render function for the sidebar list items.
   */
  customIconRender?: (page: SidebarPage) => JSX.Element | null;

  /**
   * Optional sidebar header.
   */
  header?: ReactNode;

  /**
   * Optional id to be applied to the <nav> component.
   */
  id?: string;

  /**
   * Optional flag to indicate if the project uses the sidebar layout theme.
   */
  isSidebarLayout?: boolean;

  /**
   * Optional CSS class to be applied to the <nav> component.
   */
  navClass?: string;

  /**
   * Sidebar link prefix.
   */
  pathRoot?: 'docs' | 'edit' | 'reference';

  /**
   * Optional label and badge that triggers the top-level section header
   */
  sectionHeader?: {
    badge: ReactElement | null;
    label: string | null;
  };
}

const ModuleDisabled = () => (
  <div className="disabled-warning">
    <div>
      <i aria-hidden="true" className="icon icon-alert-circle" /> Only admins can see this
    </div>
    <Button fullWidth kind="destructive" rel="noreferrer" size="sm" target="_blank" to="/dash?to=usability#modules">
      Enable it for everyone
    </Button>
  </div>
);

const PoweredBy = () => {
  const { project } = useContext(ProjectContext);
  const { subdomain } = project;
  const { t } = useTranslation();

  return (
    <div className="readme-logo" id="readmeLogo">
      {t('poweredBy')}
      &nbsp;
      <a aria-label="ReadMe" href={`https://readme.com?ref_src=hub&project=${subdomain}`} style={{ color: 'unset' }}>
        <svg className="readme-logo-icon" height="50" viewBox="0 0 293 50" width="293">
          <path d="M114.851 9.35c-9.218 0-15.76 6.482-15.76 15.522 0 9.456 7.196 15.462 16.354 15.462 4.316 0 10.001-1.388 13.158-5.3a1 1 0 0 0-.062-1.345l-2.987-2.942a.978.978 0 0 0-1.37-.01c-2.438 2.415-5.548 3.352-8.382 3.352-4.401 0-8.089-2.438-8.564-7.374h22.231a.492.492 0 0 0 .49-.422c.077-.579.116-1.18.116-1.898 0-4.519-1.606-8.206-4.342-10.882-2.676-2.677-6.482-4.163-10.882-4.163Zm-7.554 12.429c.536-3.985 3.331-6.423 7.375-6.423 2.141 0 3.985.714 5.293 2.082 1.011 1.13 1.784 2.557 2.022 4.34h-14.69ZM286.422 13.513c-2.675-2.676-6.482-4.163-10.882-4.163-9.218 0-15.76 6.482-15.76 15.522 0 9.456 7.196 15.462 16.354 15.462 4.316 0 10.001-1.388 13.159-5.3a1.002 1.002 0 0 0-.062-1.345l-2.988-2.942a.978.978 0 0 0-1.37-.01c-2.437 2.415-5.548 3.352-8.382 3.352-4.401 0-8.088-2.438-8.563-7.374h22.23a.493.493 0 0 0 .491-.422c.076-.579.115-1.18.115-1.897 0-4.52-1.606-8.207-4.342-10.883Zm-18.436 8.266c.536-3.985 3.331-6.423 7.375-6.423 2.141 0 3.985.714 5.293 2.082 1.011 1.13 1.784 2.557 2.022 4.34h-14.69ZM167.264 33.54l.003-.039h-1.541V11.238c0-.682-.552-1.234-1.233-1.234h-4.561c-.682 0-1.234.553-1.234 1.234v2.146c-2.615-2.491-6.119-4.034-10.158-4.034-8.921 0-15.165 6.66-15.165 15.522 0 8.741 6.186 15.462 15.165 15.462 4.579 0 8.087-1.903 10.585-4.757v1.202a2.961 2.961 0 0 0 2.961 2.96h4.987c.681 0 1.234-.552 1.234-1.233v-3.77c0-.614-.456-1.1-1.043-1.196Zm-17.416-.164c-4.876 0-8.385-3.747-8.385-8.504 0-4.817 3.509-8.564 8.385-8.564 4.401 0 8.266 3.092 8.266 8.564 0 5.411-3.865 8.504-8.266 8.504ZM257.585 33.502h-2.222V22.077c0-7.613-4.817-12.727-12.192-12.727-4.459 0-7.909 1.843-10.228 4.58-2.021-2.915-5.293-4.58-9.515-4.58s-7.362 1.808-9.445 4.496v-2.667c0-.681-.552-1.234-1.233-1.234h-4.561c-.682 0-1.234.553-1.234 1.234v26.249a2.312 2.312 0 0 0 2.312 2.311h8.173c.681 0 1.233-.552 1.233-1.233v-3.77c0-.682-.552-1.234-1.233-1.234h-2.218v-10.77c0-3.866 2.497-6.305 6.124-6.305 3.569 0 5.65 2.438 5.65 6.304v14.697a2.312 2.312 0 0 0 2.312 2.311h7.307c.678 0 1.228-.55 1.228-1.228V34.73c0-.678-.55-1.228-1.228-1.228h-1.352v-10.77c0-3.866 2.557-6.304 6.125-6.304s5.709 2.438 5.709 6.304V33.5l.004 3.083v.843a2.311 2.311 0 0 0 2.311 2.312h8.173c.682 0 1.234-.552 1.234-1.234v-3.77c0-.681-.553-1.234-1.234-1.234ZM199.36.005h-6.647c-.681 0-1.233.553-1.233 1.234v3.77c0 .682.552 1.234 1.233 1.234h.353l-.001.007h.99v6.074c-2.438-1.844-5.471-2.974-8.921-2.974-8.919 0-15.164 6.66-15.164 15.522 0 8.741 6.185 15.462 15.164 15.462 4.328 0 7.688-1.712 10.153-4.307v2.478c0 .682.552 1.234 1.233 1.234h4.561c.682 0 1.234-.552 1.234-1.234V34.36l.006-.001V2.966a2.961 2.961 0 0 0-2.961-2.96Zm-12.917 33.371c-4.877 0-8.385-3.747-8.385-8.504 0-4.817 3.508-8.564 8.385-8.564 4.401 0 8.266 3.092 8.266 8.564 0 5.412-3.865 8.504-8.266 8.504ZM54.631 7.158H40.196c-5.072 0-9.307 3.551-10.375 8.3-1.068-4.749-5.304-8.3-10.375-8.3H5.01A5.01 5.01 0 0 0 0 12.168v25.4a5.01 5.01 0 0 0 5.01 5.01h9.579c9.99.031 13.266 2.377 14.844 7.172a.428.428 0 0 0 .388.255.424.424 0 0 0 .387-.255c1.578-4.795 4.854-7.14 14.844-7.171h9.58a5.01 5.01 0 0 0 5.01-5.01V12.167a5.01 5.01 0 0 0-5.01-5.01Zm-29.606 27.21c0 .294-.24.534-.534.534H8.175a.535.535 0 0 1-.535-.535v-2.03c0-.296.24-.535.535-.535H24.49c.295 0 .534.24.534.535v2.03Zm0-6.32c0 .294-.24.534-.534.534H8.175a.535.535 0 0 1-.535-.535v-2.03c0-.296.24-.535.535-.535H24.49c.295 0 .534.24.534.535v2.03Zm0-6.32c0 .294-.24.534-.534.534H8.175a.535.535 0 0 1-.535-.535v-2.03c0-.296.24-.535.535-.535H24.49c.295 0 .534.24.534.535v2.03Zm26.976 12.64c0 .294-.24.534-.535.534H35.151a.535.535 0 0 1-.535-.535v-2.03c0-.296.24-.535.535-.535h16.315c.296 0 .535.24.535.535v2.03Zm0-6.32c0 .294-.24.534-.535.534H35.151a.535.535 0 0 1-.535-.535v-2.03c0-.296.24-.535.535-.535h16.315c.296 0 .535.24.535.535v2.03Zm0-6.32c0 .294-.24.534-.535.534H35.151a.535.535 0 0 1-.535-.535v-2.03c0-.296.24-.535.535-.535h16.315c.296 0 .535.24.535.535v2.03ZM100.322 10.883c-.967-.651-3.238-1.75-7.368-1.533-3.743.196-6.661 1.963-8.623 5.114v-.594l-.002-.022v-.884a2.96 2.96 0 0 0-2.96-2.961h-6.45c-.68 0-1.233.552-1.233 1.234v3.896c0 .68.553 1.233 1.234 1.233h1.137-.48.664l.002.001h1.367v17.134h-2.216c-.675 0-1.222.547-1.222 1.222v3.794c0 .675.547 1.222 1.222 1.222h12.693c.674 0 1.22-.547 1.22-1.221v-3.795c0-.675-.546-1.222-1.22-1.222h-2.211v-9.759c0-4.163 2.444-6.996 6.363-7.196 1.825-.092 3.115.187 3.906.453.22.075.45-.037.548-.249a16.908 16.908 0 0 1 3.676-5.155.454.454 0 0 0-.048-.712h.001Z" />
        </svg>
      </a>
    </div>
  );
};

// helper to check if the category contains the active doc so we can expand the category on page load
const hasActiveDoc = (pages: SidebarPage[], slug?: string): boolean => {
  if (!slug) return false;

  return pages.some(page => {
    if (page.slug === slug) return true;

    const hasNestedPages = 'pages' in page && Array.isArray(page.pages);
    return hasNestedPages && hasActiveDoc(page.pages, slug);
  });
};

const IPAD_MINI_PORTRAIT = '768px';

const Sidebar = memo(function Sidebar({
  activeDoc,
  categories,
  className,
  customIconRender,
  header,
  pathRoot,
  navClass = '',
  id,
  isSidebarLayout,
  sectionHeader,
}: SidebarProps) {
  const { project } = useContext(ProjectContext);
  const [isSuperHub, isSuperHubAdminOrEditor, routeSection, isEnterpriseChild, isAdminUIHidden] = useSuperHubStore(
    s => [
      s.isSuperHub,
      s.user.role === 'admin' || s.user.role === 'editor',
      s.routeSection,
      s.enterprise?.isChild,
      s.isAdminUIHidden,
    ],
  );
  const [hideReadmeLogo, activePlan, collapsibleCategoriesEnabled] = useProjectStore(s => [
    s.data.appearance.footer.readme_logo === 'hide',
    s.plan.activePlan,
    s.data.appearance.navigation.collapsible_categories === 'enabled',
  ]);
  const projectCanHideLogo = usePlanPermissions(activePlan, 'whitelabel');
  const parentHidesReadmeLogo = !!(isEnterpriseChild && project.parent?.appearance?.hide_logo);
  const showPoweredByLogo = !(projectCanHideLogo && (hideReadmeLogo || parentHidesReadmeLogo));

  const { scrollAreaRef, scrollContentRef, activeItemRef } = useScrollToActiveSidebarItem();
  const isMobile = useMediaQuery(`(max-width: ${IPAD_MINI_PORTRAIT})`);
  const isAskAIViewport = useMediaQuery('(min-width: 1400px) and (max-width: 1800px)');
  const bem = useClassy(classes, 'Nav');

  const storage = useLocalStorage({ json: true, prefix: 'sidebar-collapsed' });
  const isOwlbotOpen = useSuperHubStore(s => s.isOwlbotOpenCached);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(false);
    } else {
      setIsCollapsed(storage.getItem('sidebar-collapsed') || false);
    }
  }, [isMobile, storage]);

  // Auto-collapse reference sidebar when Ask AI is open at 1400-1800px
  useEffect(() => {
    if (id !== 'reference-sidebar') return;

    if (isAskAIViewport && isOwlbotOpen) {
      // Auto-collapse when Ask AI opens in the target viewport range
      setIsCollapsed(true);
    } else if (!isAskAIViewport && isOwlbotOpen) {
      // When exiting the Ask AI viewport range (e.g., resizing to >1800px) while Ask AI is still open,
      // restore the user's previous preference
      setIsCollapsed(storage.getItem('sidebar-collapsed') || false);
    } else if (isAskAIViewport && !isOwlbotOpen) {
      // When Ask AI closes within the target viewport, restore the user's previous preference
      setIsCollapsed(storage.getItem('sidebar-collapsed') || false);
    }
  }, [id, isAskAIViewport, isOwlbotOpen, storage]);

  /**
   * If the user is a superhub admin or editor and NOT in preview mode, show all categories.
   * Otherwise (non-admin/editor OR in preview mode), only show categories with visible pages.
   */
  const filteredCategories = useMemo(() => {
    const orderedCategories = [...categories];

    // Show all pages if admin and NOT in preview mode
    const shouldShowHidden = isSuperHubAdminOrEditor && !isAdminUIHidden;

    if (shouldShowHidden) {
      return orderedCategories;
    }

    const hasNestedPages = (page: SidebarPage): page is SidebarPage & { pages: SidebarPage[] } => {
      return 'pages' in page && Array.isArray(page.pages);
    };

    // Recursively filter hidden pages at all nesting levels
    const filterPages = (pages: SidebarPage[]): SidebarPage[] => {
      return pages
        .filter(page => !page.hidden)
        .map(page => {
          if (hasNestedPages(page)) {
            return {
              ...page,
              pages: filterPages(page.pages),
            };
          }
          return page;
        }) as SidebarPage[];
    };

    // Filter out hidden pages and empty categories
    return orderedCategories
      .map(category => ({
        ...category,
        pages: filterPages(category.pages),
      }))
      .filter(category => category.pages.length > 0);
  }, [categories, isSuperHubAdminOrEditor, isAdminUIHidden]);

  const SidebarToggle = useMemo(() => {
    const toggleSidebar = (collapsed: boolean) => {
      setIsCollapsed(collapsed);
      storage.setItem('sidebar-collapsed', collapsed);
    };

    return isCollapsed ? (
      <Button
        aria-label="Show sidebar navigation"
        className={bem('-toggle-expand')}
        ghost
        kind="secondary"
        onClick={() => toggleSidebar(false)}
        size="sm"
      >
        <Icon aria-hidden="true" name="sidebar" size="md" />
      </Button>
    ) : (
      <button
        aria-label="Hide sidebar navigation"
        className={bem('-toggle-collapse')}
        onClick={() => toggleSidebar(true)}
        type="button"
      />
    );
  }, [isCollapsed, bem, storage]);

  const navClasses = bem(
    'rm-Sidebar',
    'hub-sidebar',
    'reference-redesign',
    className,
    '&',
    isSidebarLayout && '_sidebar-layout',
    !isSidebarLayout && pathRoot === 'docs' && ' rm-Sidebar_guides',
    navClass && navClass,
    isMobile && classes.Sidebar_flyout,
    isCollapsed && bem('-collapsed'),
  );

  const categoriesArray = () => {
    return filteredCategories.map(({ title, pages, slug, uri }) => {
      const hideCategoryTitle =
        isSuperHub && pathRoot !== 'edit' && isApiConfigCategory(pathRoot || routeSection, title, pages);
      const isRealtimeCategory = pages.some(
        page => ('pageType' in page && page.pageType === 'RealtimePage') || page.type === 'api_config',
      );
      const isCategoryCollapsible = collapsibleCategoriesEnabled && !isRealtimeCategory;
      const key = slug ?? uri ?? title;
      const isCategoryOpen = expandedCategories[key] ?? hasActiveDoc(pages, activeDoc);

      return (
        <section
          key={slug ?? uri}
          className={classy(
            classes['Sidebar-listWrapper'],
            isCategoryCollapsible && !hideCategoryTitle && classes['Sidebar-listWrapper_collapsible'],
            isCategoryOpen && classes['Sidebar-listWrapper_expanded'],
            'rm-Sidebar-section',
          )}
        >
          {!hideCategoryTitle && (
            <>
              {isCategoryCollapsible ? (
                <button
                  aria-label={`${isCategoryOpen ? 'Collapse' : 'Expand'} ${title} category`}
                  className={classy(
                    classes['Sidebar-heading'],
                    classes['Sidebar-heading_collapsible'],
                    isCategoryOpen && classes['Sidebar-heading_expanded'],
                    'rm-Sidebar-category',
                  )}
                  onClick={() => setExpandedCategories(prev => ({ ...prev, [key]: !isCategoryOpen }))}
                  type="button"
                >
                  {title}
                  <Icon aria-hidden="true" className={classes['Sidebar-heading-icon']} name="chevron-right" size="md" />
                </button>
              ) : (
                <h2
                  className={classy(
                    classes['Sidebar-heading'],
                    isSidebarLayout && classes['Sidebar-heading_sidebarLayout'],
                    'rm-Sidebar-heading',
                  )}
                >
                  {title}
                </h2>
              )}
            </>
          )}
          <SidebarList
            activeItemRef={activeItemRef}
            alwaysExpanded={isMobile}
            className={bem('Sidebar-list_sidebarLayout')}
            isCategoryExpanded={isCategoryOpen}
            isCollapsible={isCategoryCollapsible}
            {...{ activeDoc, customIconRender, pathRoot, pages }}
          />
        </section>
      );
    });
  };

  if (isSidebarLayout) {
    return <Group sectionHeader={sectionHeader}>{Array.isArray(filteredCategories) && categoriesArray()}</Group>;
  }

  return (
    <nav
      ref={scrollAreaRef}
      aria-label="Secondary navigation"
      className={navClasses}
      id={id || 'hub-sidebar'}
      role="navigation"
    >
      {!isCollapsed && header}

      <div
        ref={scrollContentRef}
        className={classy(
          classes.Sidebar,
          isCollapsed && classes['Sidebar-collapsed'],
          'rm-Sidebar',
          'hub-sidebar-content',
        )}
      >
        {!isSuperHub && !(project?.modules?.[pathRoot || ''] || pathRoot === 'edit') && <ModuleDisabled />}
        {Array.isArray(filteredCategories) && categoriesArray()}
        {!!showPoweredByLogo && <PoweredBy />}
      </div>

      {SidebarToggle}
    </nav>
  );
});

export default Sidebar;
